import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@component/table";
import { Badge } from "@component/badge";
import { Button } from "@component/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@component/dropdown-menu";
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  BriefcaseIcon,
  ClockIcon,
  CopyIcon,
  EyeIcon,
  AlertCircleIcon,
  FileUp,
  FileTextIcon,
  GraduationCapIcon,
  MapPinIcon,
  MessageCircleIcon,
  MoreVerticalIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  RefreshCwIcon,
  ShareIcon,
  TrashIcon,
  UsersIcon,
} from "lucide-react";
import { Job } from "@type/job";
import { SendCustomizedEmailRequest } from "@type/candidate";
import { getStatusBadgeClass, shortLocation } from "@util/string";
import { cn } from "@util/tailwind";
import { useUserProfileContext } from "@context/user_profile";
import { useSnackbar } from "@hook/use-snackbar";
import { getSubdomainHost } from "@util/name";
import { sendCustomizedEmailV2 } from "@service/employer";
import { duplicateJob } from "@service/job";
import { useSubscription } from "@context/subscription_context";
import { catchErrorLog } from "@util/sentry";
import { Tooltip } from "./tooltip";
import NotificationMessage from "./notification-message";
import VersionHistoryDialog from "./job-version-history-dialog";
import CloseJobConfirmationDialog from "@container/dialog/dialog-close-job-confirm";
import DeleteJobConfirmationDialog from "@container/dialog/dialog-delete-job-confirm";
import JobNotifyCandidateDialog from "./job-notify-candidate-dialog";
import WhatsappAdLinkDialog from "@container/dialog/dialog-whatsapp-ad-link";

// ── Types ────────────────────────────────────────────────────────────────────

interface JobTableViewProps {
  jobs: Job[];
  onJobAction: (job: Job) => void;
  onStatusChange: (
    jobId: string,
    status: string,
    is_public?: boolean | undefined
  ) => void;
  onCloseJob: (job: Job) => void;
  onDelete: (job: Job) => void;
}

type SortDir = "asc" | "desc";

interface TableSortState {
  key: string;
  dir: SortDir;
}

interface ColumnDef {
  key: string;
  label: string;
  sortable: boolean;
}

// ── Column definitions ───────────────────────────────────────────────────────

const COLUMNS: ColumnDef[] = [
  { key: "job_title", label: "Job Title", sortable: true },
  { key: "location", label: "Location", sortable: false },
  { key: "applicants_count", label: "Applicants", sortable: true },
  { key: "days_active", label: "Days Active", sortable: true },
  { key: "created_at", label: "Posted", sortable: true },
  { key: "updated_at", label: "Updated", sortable: true },
  { key: "job_owner", label: "Owner", sortable: true },
  { key: "job_status", label: "Status", sortable: true },
  { key: "_actions", label: "", sortable: false },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDaysActive(createdAt: string): number {
  return Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / 86400000
  );
}

function formatCompactDate(dateStr: string): string {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  return `${day} ${month} ${date.getFullYear()}`;
}

function getDisplayStatus(jobStatus: string): string {
  if (jobStatus === "Published") return "Active";
  if (jobStatus === "Deleted") return "Closed";
  return jobStatus;
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Sub-components ───────────────────────────────────────────────────────────

function DaysActiveChip({ createdAt }: { createdAt: string }) {
  const days = getDaysActive(createdAt);

  return (
    <span
      className={cn(
        "inline-flex items-center text-xs font-medium px-2 py-0.5 rounded",
        days > 30
          ? "bg-red-100 text-red-700"
          : days > 14
            ? "bg-amber-100 text-amber-700"
            : "bg-gray-200 text-gray-600"
      )}
    >
      {days}d
    </span>
  );
}

function OwnerAvatar({ name }: { name: string }) {
  const initials = getInitials(name);

  return (
    <Tooltip content={<p>{name}</p>} delayDuration={100}>
      <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-xs font-semibold flex-shrink-0 cursor-default">
        {initials}
      </div>
    </Tooltip>
  );
}

function JobTypeIcon({ jobType }: { jobType: string }) {
  const normalized = jobType?.toLowerCase().trim() || "";

  let Icon = BriefcaseIcon;
  let colorClass = "text-blue-500 bg-blue-50 border-blue-200";
  let label = jobType || "Full time";

  if (normalized.includes("contract") || normalized.includes("freelance")) {
    Icon = FileTextIcon;
    colorClass = "text-orange-500 bg-orange-50 border-orange-200";
    label = jobType;
  } else if (normalized.includes("intern")) {
    Icon = GraduationCapIcon;
    colorClass = "text-purple-500 bg-purple-50 border-purple-200";
    label = jobType;
  } else if (normalized.includes("part")) {
    Icon = ClockIcon;
    colorClass = "text-teal-500 bg-teal-50 border-teal-200";
    label = jobType;
  }

  return (
    <Tooltip content={<p>{label}</p>} delayDuration={100}>
      <div
        className={cn(
          "flex items-center justify-center w-7 h-7 rounded-md border flex-shrink-0 cursor-default",
          colorClass
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
    </Tooltip>
  );
}

function SortableTableHead({
  column,
  sortState,
  onToggle,
}: {
  column: ColumnDef;
  sortState: TableSortState | null;
  onToggle: (key: string) => void;
}) {
  const isActive = sortState?.key === column.key;
  const dir = isActive ? sortState.dir : null;

  if (!column.label) {
    return <TableHead className="w-[50px]" />;
  }

  return (
    <TableHead
      className={cn(
        "text-xs font-medium uppercase tracking-wider whitespace-nowrap",
        column.sortable &&
          "cursor-pointer select-none hover:text-gray-700",
        isActive && "text-blue-600",
        column.key !== "job_title" && column.key !== "applicants_count" && column.key !== "location" && "text-center"
      )}
      onClick={() => column.sortable && onToggle(column.key)}
    >
      <span className="inline-flex items-center gap-1">
        {column.label}
        {column.sortable &&
          (dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : dir === "desc" ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3 text-gray-300" />
          ))}
      </span>
    </TableHead>
  );
}

function JobTableRow({
  job,
  onJobAction,
  onStatusChange,
  onCloseJob,
  onDelete,
}: {
  job: Job;
  onJobAction: (job: Job) => void;
  onStatusChange: (
    jobId: string,
    status: string,
    is_public?: boolean | undefined
  ) => void;
  onCloseJob: (job: Job) => void;
  onDelete: (job: Job) => void;
}) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [isLoadingUserProfile, userInfo] = useUserProfileContext();
  const { showTrialUsageLimitDialog, subscriptionActive, isWorkspaceRestricted, isJobActionBlocked } = useSubscription();
  // Trial-only: paid plans ignore CV/AI limit gates entirely.
  const isJobLimitReached = !!subscriptionActive?.is_trial && (!!subscriptionActive?.is_cv_limit_reached || !!subscriptionActive?.is_ai_limit_reached);
  const expiredTooltip = isWorkspaceRestricted ? "Your trial has expired. Upgrade to manage jobs." : undefined;
  const [isOpenCloseJobDialog, setIsOpenCloseJobDialog] =
    useState<boolean>(false);
  const [isOpenDeleteJobDialog, setIsOpenDeleteJobDialog] =
    useState<boolean>(false);
  const [isOpenNotifyCandidateDialog, setIsOpenNotifyCandidateDialog] =
    useState<boolean>(false);
  const [notificationType, setNotificationType] = useState<string>("");
  const [notificationMessage, setNotificationMessage] =
    useState<string>("");
  const [isOpenVersionHistoryDialog, setIsOpenVersionHistoryDialog] =
    useState<boolean>(false);
  // #3251: "Copy WhatsApp link" opens the ad-link dialog (link + prefilled
  // message preview + copy) — whatsapp_first jobs only.
  const [isOpenWhatsappAdLinkDialog, setIsOpenWhatsappAdLinkDialog] =
    useState<boolean>(false);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);

  const displayStatus = getDisplayStatus(job.job_status);

  // #3443 (BE #4483): clone the job into a new listing (created Active right
  // away, per product decision), then land HR on its edit page to rename the copy.
  const handleDuplicate = async () => {
    if (isDuplicating) return;
    setIsDuplicating(true);
    try {
      const newJobId = await duplicateJob(job.id);
      if (newJobId) {
        snackbar.showSuccess("Listing duplicated");
        navigate(`/jobs/edit/${newJobId}`);
      } else {
        snackbar.showError("Failed to duplicate listing. Please try again later.");
      }
    } catch (error) {
      console.error("Failed to duplicate job:", error);
      snackbar.showError("Failed to duplicate listing. Please try again later.");
    } finally {
      setIsDuplicating(false);
    }
  };

  const onShare = async () => {
    setNotificationType("");
    setNotificationMessage("");
    const candidateProfileLink = `${getSubdomainHost(job.company_subdomain || "")}/job/${job.id}`;
    try {
      await navigator.clipboard.writeText(candidateProfileLink);
      setNotificationType("success");
      setNotificationMessage(
        `Job ${job.job_title} copied to clipboard.`
      );
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  const handleSendNotifyCandidate = async (
    body: SendCustomizedEmailRequest
  ) => {
    sendCustomizedEmailV2(body)
      .then((resp) => {
        if (resp.status === 200) {
          snackbar.showSuccess(resp.data.message);
          onCloseJob(job);
        }
      })
      .catch((error) => {
        catchErrorLog(
          `Failed to send interview invitation: ${error}`
        );
        if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
          showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
        } else if (error.response?.data?.message) {
          snackbar.showError(error.response.data.message);
        }
      })
      .finally(() => {
        setIsOpenNotifyCandidateDialog(false);
      });
  };

  return (
    <>
      {notificationType !== "" && (
        <NotificationMessage
          duration={15000}
          type={notificationType}
          message={notificationMessage}
        />
      )}
      <TableRow
        className="cursor-pointer hover:bg-blue-50/50"
        onClick={() => navigate(`/jobs/${job.id}/applications`)}
      >
        {/* Job Title — wide cap so long names rarely truncate (tooltip still
            carries the full title); the compacted Location column pays for it. */}
        <TableCell className="min-w-[220px] max-w-[440px]">
          <div className="flex items-center gap-2.5">
            <JobTypeIcon jobType={job.job_type} />
            <div className="min-w-0">
              <Tooltip
                content={job.job_title}
                side="top"
              >
                <div className="text-sm font-medium text-gray-900 truncate">
                  {job.job_title}
                </div>
              </Tooltip>
              <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1 min-w-0">
                {/* WhatsApp-first flow marker — candidates run this job's flow in WA. */}
                {job.hiring_flow?.channel === "whatsapp_first" && (
                  <Tooltip content="WhatsApp-first hiring flow">
                    <MessageCircleIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
                  </Tooltip>
                )}
                <span className="truncate">
                  {[job.company_name, job.department]
                    .filter(Boolean)
                    .join(" · ") || "No Department"}
                </span>
              </div>
            </div>
          </div>
        </TableCell>

        {/* Location — "city, country" only; the full value stays on hover. */}
        <TableCell className="text-xs text-gray-600 min-w-[140px]">
          {shortLocation(job.location) !== (job.location || "").trim() ? (
            <Tooltip content={job.location}>
              <span>{shortLocation(job.location) || "No Location"}</span>
            </Tooltip>
          ) : (
            shortLocation(job.location) || "No Location"
          )}
        </TableCell>

        {/* Applicants */}
        <TableCell className="whitespace-nowrap">
          <div className="inline-flex items-center gap-3">
            {(job.total_applied ?? 0) > 0 && (
              <Tooltip content="Applied" side="top">
                <span className="inline-flex items-center gap-1 text-xs">
                  <UsersIcon className="h-3.5 w-3.5 text-blue-600" />
                  <span className="font-medium text-gray-600">
                    {job.total_applied}
                  </span>
                </span>
              </Tooltip>
            )}
            {(job.total_upload ?? 0) > 0 && (
              <Tooltip content="Uploaded" side="top">
                <span className="inline-flex items-center gap-1 text-xs">
                  <FileUp className="h-3.5 w-3.5 text-blue-600" />
                  <span className="font-medium text-gray-600">
                    {job.total_upload}
                  </span>
                </span>
              </Tooltip>
            )}
            {(job.total_review ?? 0) > 0 && (
              <Tooltip content="Pending Review" side="top">
                <span className="inline-flex items-center gap-1 text-xs">
                  <AlertCircleIcon className="h-3.5 w-3.5 text-blue-600" />
                  <span className="font-medium text-gray-600">
                    {job.total_review}
                  </span>
                </span>
              </Tooltip>
            )}
          </div>
        </TableCell>

        {/* Days Active */}
        <TableCell className="text-center">
          <DaysActiveChip createdAt={job.created_at} />
        </TableCell>

        {/* Posted */}
        <TableCell className="whitespace-nowrap text-xs text-gray-500 text-center">
          {formatCompactDate(job.created_at)}
        </TableCell>

        {/* Updated */}
        <TableCell className="whitespace-nowrap text-xs text-gray-500 text-center">
          {job.updated_at ? formatCompactDate(job.updated_at) : "—"}
        </TableCell>

        {/* Owner */}
        <TableCell className="text-center">
          <OwnerAvatar name={job.job_owner || job.hr_name || "Unknown"} />
        </TableCell>

        {/* Status */}
        <TableCell className="text-center">
          <Badge
            className={cn(
              "!shadow-none rounded-xl",
              getStatusBadgeClass(displayStatus)
            )}
          >
            {displayStatus}
          </Badge>
        </TableCell>

        {/* Actions */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          {!isLoadingUserProfile && job.job_status !== "Deleted" && job.job_status !== "Closed" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <MoreVerticalIcon className="h-4 w-4" />
                  <span className="sr-only">More options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!job.is_collaborator &&
                  userInfo?.permission !== "member" && (
                    <DropdownMenuItem
                      className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => onJobAction(job)}
                      disabled={isJobActionBlocked}
                      title={isJobActionBlocked ? expiredTooltip : undefined}
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit listing
                    </DropdownMenuItem>
                  )}
                {!job.is_collaborator &&
                  userInfo?.permission !== "member" && (
                    <DropdownMenuItem
                      className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleDuplicate()}
                      disabled={isWorkspaceRestricted || isDuplicating}
                      title={isWorkspaceRestricted ? expiredTooltip : undefined}
                    >
                      <CopyIcon className="h-4 w-4 mr-2" />
                      {isDuplicating ? "Duplicating…" : "Duplicate listing"}
                    </DropdownMenuItem>
                  )}
                {job.is_collaborator && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onJobAction(job)}
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View listing
                  </DropdownMenuItem>
                )}
                {!job.is_collaborator &&
                  userInfo?.permission !== "member" && (
                    <>
                      {(job.job_status === "Active" ||
                        job.job_status === "Published") &&
                        ["hr", "super_admin"].includes(
                          userInfo?.role || ""
                        ) && (
                          <DropdownMenuItem
                            className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() =>
                              onStatusChange(job.id, "Paused")
                            }
                            disabled={isWorkspaceRestricted}
                            title={expiredTooltip}
                          >
                            <PauseIcon className="h-4 w-4 mr-2" />
                            Pause listing
                          </DropdownMenuItem>
                        )}
                      {job.job_status === "Paused" &&
                        ["hr", "super_admin"].includes(
                          userInfo?.role || ""
                        ) && !subscriptionActive?.is_cv_screening_limit_reached && (
                          <DropdownMenuItem
                            className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() =>
                              onStatusChange(job.id, "Published")
                            }
                            disabled={isJobLimitReached || isWorkspaceRestricted}
                            title={isWorkspaceRestricted ? expiredTooltip : undefined}
                          >
                            <PlayIcon className="h-4 w-4 mr-2" />
                            Activate listing
                          </DropdownMenuItem>
                        )}
                    </>
                  )}
                {/* #3251: WhatsApp-first jobs get "Copy WhatsApp link" INSTEAD of
                    "Copy listing URL" (candidates enter via WhatsApp, not the web
                    listing); every other job keeps the listing URL. */}
                {job.hiring_flow?.channel === "whatsapp_first" ? (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => setIsOpenWhatsappAdLinkDialog(true)}
                  >
                    <MessageCircleIcon className="h-4 w-4 mr-2 text-green-600" />
                    Copy WhatsApp link
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onShare()}
                  >
                    <ShareIcon className="h-4 w-4 mr-2" />
                    Copy listing URL
                  </DropdownMenuItem>
                )}
                {!job.is_collaborator && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() =>
                      setIsOpenVersionHistoryDialog(true)
                    }
                  >
                    <RefreshCwIcon className="h-4 w-4 mr-2" />
                    Show version history
                  </DropdownMenuItem>
                )}
                {!job.is_collaborator &&
                  (["hr", "super_admin"].includes(
                    userInfo?.role || ""
                  ) ||
                    userInfo?.permission !== "member") && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() =>
                          setIsOpenCloseJobDialog(true)
                        }
                        disabled={isWorkspaceRestricted}
                        title={expiredTooltip}
                        className="text-red-600 focus:text-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Close listing
                      </DropdownMenuItem>
                    </>
                  )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isLoadingUserProfile && (job.job_status === "Deleted" || job.job_status === "Closed") && !job.is_collaborator &&
            (["hr", "super_admin"].includes(userInfo?.role || "") || userInfo?.permission !== "member") && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <MoreVerticalIcon className="h-4 w-4" />
                  <span className="sr-only">More options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setIsOpenDeleteJobDialog(true)}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete listing
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>

      {/* Dialogs — rendered per-row, same as JobCard */}
      <VersionHistoryDialog
        job={job}
        isOpen={isOpenVersionHistoryDialog}
        onClose={() => setIsOpenVersionHistoryDialog(false)}
      />

      <WhatsappAdLinkDialog
        isOpen={isOpenWhatsappAdLinkDialog}
        jobId={job.id}
        onClose={() => setIsOpenWhatsappAdLinkDialog(false)}
      />


      <CloseJobConfirmationDialog
        isOpen={isOpenCloseJobDialog}
        countApplicants={job.applicants_count || 0}
        onClose={() => setIsOpenCloseJobDialog(false)}
        onConfirmWithoutEmail={() => {
          setIsOpenCloseJobDialog(false);
          onCloseJob(job);
        }}
        onConfirmWithEmail={() => {
          setIsOpenCloseJobDialog(false);
          setIsOpenNotifyCandidateDialog(true);
        }}
      />

      <JobNotifyCandidateDialog
        isOpen={isOpenNotifyCandidateDialog}
        job={job}
        onClose={() => setIsOpenNotifyCandidateDialog(false)}
        onSendNotification={handleSendNotifyCandidate}
      />

      <DeleteJobConfirmationDialog
        isOpen={isOpenDeleteJobDialog}
        job={job}
        onClose={() => setIsOpenDeleteJobDialog(false)}
        onConfirm={(job: Job) => {
          setIsOpenDeleteJobDialog(false);
          snackbar.showSuccess("Job listing successfully deleted.");
          onDelete(job);
        }}
      />
    </>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function JobTableView({
  jobs,
  onJobAction,
  onStatusChange,
  onCloseJob,
  onDelete,
}: JobTableViewProps) {
  const [sortState, setSortState] = useState<TableSortState | null>(
    null
  );

  const toggleSort = (key: string) => {
    setSortState((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const sortedJobs = useMemo(() => {
    if (!sortState) return jobs;

    return [...jobs].sort((a, b) => {
      let va: string | number;
      let vb: string | number;

      switch (sortState.key) {
        case "job_title":
          va = a.job_title.toLowerCase();
          vb = b.job_title.toLowerCase();
          break;
        case "applicants_count":
          va = a.applicants_count;
          vb = b.applicants_count;
          break;
        case "days_active":
          // Fewer days active = more recent created_at (higher timestamp)
          // asc (fewest days first) = sort by created_at desc
          va = new Date(a.created_at).getTime();
          vb = new Date(b.created_at).getTime();
          return sortState.dir === "asc" ? vb - va : va - vb;
        case "created_at":
          va = new Date(a.created_at).getTime();
          vb = new Date(b.created_at).getTime();
          break;
        case "updated_at":
          va = new Date(a.updated_at).getTime();
          vb = new Date(b.updated_at).getTime();
          break;
        case "job_owner":
          va = (a.job_owner || a.hr_name || "").toLowerCase();
          vb = (b.job_owner || b.hr_name || "").toLowerCase();
          break;
        case "job_status":
          va = a.job_status.toLowerCase();
          vb = b.job_status.toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof va === "string" && typeof vb === "string") {
        return sortState.dir === "asc"
          ? va.localeCompare(vb)
          : vb.localeCompare(va);
      }

      return sortState.dir === "asc"
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    });
  }, [jobs, sortState]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-x-auto">
      <Table className="min-w-max">
        <TableHeader>
          <TableRow className="bg-gray-50 hover:bg-gray-50">
            {COLUMNS.map((col) => (
              <SortableTableHead
                key={col.key}
                column={col}
                sortState={sortState}
                onToggle={toggleSort}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedJobs.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={COLUMNS.length}
                className="text-center py-16 text-gray-400 text-sm"
              >
                No jobs found.
              </TableCell>
            </TableRow>
          ) : (
            sortedJobs.map((job) => (
              <JobTableRow
                key={job.id}
                job={job}
                onJobAction={onJobAction}
                onStatusChange={onStatusChange}
                onCloseJob={onCloseJob}
                onDelete={onDelete}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
